import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiOutputsDeviceBehaviorPlugin } from '@shure/device-ui/outputs/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannelDspKind,
	SchematicsDeviceChannel
} from '@shure/device-ui/schematics/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DspType, DspChannelType, DspCopyChannelProperty } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { ChannelGroup, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class AniusbCDeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiOutputsDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.ANIUSBC];

	constructor(protected readonly transloco: TranslocoService) {}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedWithoutImage;
	}

	public getInputChannelGroups(): ChannelGroup[] {
		return [ChannelGroup.DanteInput, ChannelGroup.AuxInput, ChannelGroup.UsbInput];
	}

	public getOutputChannelGroups(): ChannelGroup[] {
		return [ChannelGroup.DanteOutput, ChannelGroup.AuxOutput, ChannelGroup.UsbOutput];
	}

	public isCompressorCopyPasteSupported(_model: DeviceModel): boolean {
		return true;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(([schematicLabel, inputsLabel, matrixMixerLabel, outputsLabel, presetsLabel, settingsLabel]) => [
				{
					route: DeviceUiRoute.Schematic,
					label: schematicLabel
				},
				{
					route: DeviceUiRoute.Inputs,
					label: inputsLabel
				},
				{
					route: DeviceUiRoute.Matrixmixer,
					label: matrixMixerLabel
				},
				{
					route: DeviceUiRoute.Outputs,
					label: outputsLabel
				},
				{
					route: DeviceUiRoute.Presets,
					label: presetsLabel
				},
				{
					route: DeviceUiRoute.Settings,
					label: settingsLabel
				}
			])
		);
	}

	public getDefaultRoute(): DeviceUiRoute | undefined {
		return DeviceUiRoute.Schematic;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getOutputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Limiter, 2]
		]);
	}

	public getDeviceInputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> | undefined {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteInput, 0],
			[ChannelGroup.AuxInput, 1],
			[ChannelGroup.DanteAnalogInput, 2],
			[ChannelGroup.UsbInput, 3]
		]);
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([
			['equalizer', 1],
			['limiter', 2]
		]);
	}

	public getChannelCopyPasteProperties(
		_model: DeviceModel,
		_channelType: DspChannelType,
		_selectedChannels: SchematicsDeviceChannel[]
	): DspCopyChannelProperty[] {
		return ['muted', 'gain', 'analogLevel'];
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getDirectChannelGroups(_model: DeviceModel): undefined {
		return undefined;
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return false;
	}

	public showSpatialSeparationLabels(_model: DeviceModel): boolean {
		return true;
	}

	public getAutomixedChannelGroups(): undefined {
		return undefined;
	}
}
